import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chip from '../common/Chip.jsx';
import TagSelector from '../TagSelector.jsx';
import InlineBanner from '../common/InlineBanner.jsx';
import { addTagToFile, createTag, removeTagFromFile } from '../../services/TagService.mjs';
import { updateFileMetadata } from '../../services/FileService.mjs';
import { indexTagsById, getFullPathLabel } from '../../utils/tagHierarchy';
import { IconPlus, IconSparkle, IconTag } from './icons.jsx';
import '../../css/FileTagsSection.css';

/**
 * A file's tags, and everything needed to change them, in place.
 *
 * Adding a tag used to open a second full-screen modal ON TOP of the details
 * modal — a whole dialog's worth of ceremony for a one-word edit, which also
 * hid the tags you were editing behind it. The picker is inline here, so the
 * existing chips stay visible while you add to them.
 *
 * `aiTags` (names suggested by the auto-tagger at import time) were written to
 * every bulk-imported file and then never surfaced anywhere. They are offered
 * as one-click suggestions and removed from the suggestion list once applied,
 * so the list drains as it is used rather than repeating forever.
 */
const FileTagsSection = ({ file, allTags = [], canEdit = true, onChanged }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const editorRef = useRef(null);

    const tagIds = useMemo(
        () => (Array.isArray(file?.metadata?.tags) ? file.metadata.tags : []),
        [file],
    );
    const byId = useMemo(() => indexTagsById(allTags), [allTags]);
    const byName = useMemo(
        () => new Map(allTags.map((tag) => [String(tag.name).toLowerCase(), tag])),
        [allTags],
    );

    // Suggestions already applied (or already present under a different case)
    // are noise, not suggestions.
    const suggestions = useMemo(() => {
        const raw = Array.isArray(file?.metadata?.aiTags) ? file.metadata.aiTags : [];
        const assigned = new Set(
            tagIds.map((id) => String(byId.get(id)?.name || id).toLowerCase()),
        );
        return raw.filter((name) => name && !assigned.has(String(name).toLowerCase()));
    }, [file, tagIds, byId]);

    // The picker's own dropdown is ~300px tall and the rail scrolls, so opening
    // it near the bottom would push it out of sight. Nudge it into view instead.
    useEffect(() => {
        if (!open) return;
        const id = requestAnimationFrame(() => {
            editorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    const run = async (key, work) => {
        setBusy(key);
        setError(null);
        try {
            await work();
            await onChanged?.();
        } catch (err) {
            console.error('Tag update failed:', err);
            setError(err?.response?.data?.message || 'Could not update this file’s tags.');
        } finally {
            setBusy(null);
        }
    };

    const handleRemove = (tagId) =>
        run(`remove:${tagId}`, () => removeTagFromFile(file.id, tagId));

    const handleAddExisting = (tagId) =>
        run(`add:${tagId}`, () => addTagToFile(file.id, tagId));

    const handleCreateAndAdd = (name) =>
        run(`create:${name}`, async () => {
            const tagId = await createTag(name);
            await addTagToFile(file.id, tagId);
        });

    const handleApplySuggestion = (name) =>
        run(`suggest:${name}`, async () => {
            const existing = byName.get(String(name).toLowerCase());
            const tagId = existing ? existing.id : await createTag(name);
            await addTagToFile(file.id, tagId);
            // Drain the suggestion. Best-effort: the tag is already on the file,
            // and a failure here would only mean the chip reappears.
            const remaining = (file.metadata?.aiTags || []).filter(
                (n) => String(n).toLowerCase() !== String(name).toLowerCase(),
            );
            try {
                await updateFileMetadata(file.id, { aiTags: remaining });
            } catch (err) {
                console.warn('Could not clear the applied suggestion:', err);
            }
        });

    const handleDismissSuggestions = () =>
        run('suggest:clear', () => updateFileMetadata(file.id, { aiTags: [] }));

    return (
        <div className="fts-root">
            <div className="fts-chips">
                {tagIds.map((tagId) => {
                    const tag = byId.get(tagId);
                    const name = tag?.name || tagId;
                    return (
                        <Chip
                            key={tagId}
                            color={tag?.color}
                            variant="accent-subtle"
                            title={tag ? getFullPathLabel(tagId, byId) : `Unknown tag ${tagId}`}
                            onRemove={canEdit && busy !== `remove:${tagId}` ? () => handleRemove(tagId) : undefined}
                            removeLabel={`Remove tag ${name}`}
                        >
                            {name}
                        </Chip>
                    );
                })}

                {tagIds.length === 0 && (
                    <span className="fts-empty">
                        <IconTag />
                        No tags yet
                    </span>
                )}

                {canEdit && (
                    <button
                        type="button"
                        className={`fts-add ${open ? 'is-open' : ''}`.trim()}
                        onClick={() => setOpen((v) => !v)}
                        aria-expanded={open}
                    >
                        <IconPlus width="13" height="13" />
                        {open ? 'Done' : 'Add tag'}
                    </button>
                )}
            </div>

            {error && (
                <InlineBanner variant="danger" onDismiss={() => setError(null)}>{error}</InlineBanner>
            )}

            {open && canEdit && (
                <div className="fts-editor" ref={editorRef}>
                    <TagSelector
                        initialTags={tagIds}
                        onExistingTagAdded={handleAddExisting}
                        onNewTagAdded={handleCreateAndAdd}
                    />
                </div>
            )}

            {canEdit && suggestions.length > 0 && (
                <div className="fts-suggestions">
                    <div className="fts-suggestions-head">
                        <span className="fts-suggestions-title">
                            <IconSparkle width="13" height="13" />
                            Suggested by AI at import
                        </span>
                        <button
                            type="button"
                            className="fts-suggestions-clear"
                            onClick={handleDismissSuggestions}
                            disabled={busy === 'suggest:clear'}
                        >
                            Dismiss all
                        </button>
                    </div>
                    <div className="fts-chips">
                        {suggestions.map((name) => (
                            <button
                                key={name}
                                type="button"
                                className="fts-suggestion"
                                onClick={() => handleApplySuggestion(name)}
                                disabled={busy === `suggest:${name}`}
                                title={`Add “${name}” as a tag`}
                            >
                                <IconPlus width="12" height="12" />
                                {name}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileTagsSection;
